import { DANGER, PRIMARY } from '../Theme/palette';
import { Layout, Tooltip, Typography } from 'antd';
import {
  EditOutlined,
  LogoutOutlined,
  UpOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@stores';
import { L } from '@lib';
import { BigLogo, SmallLogo } from './Assets/LogoSvg';
import { SiderMenu } from './SiderMenu';
import styled from 'styled-components';

const { Sider } = Layout;
const { Text } = Typography;

export interface ISiderMenuProps {
  collapsed: boolean;
  /** Flips the sider; it takes no argument - the host owns the state and toggles it. */
  onCollapse: () => void;
}

const SiderWrapper = styled(Sider)`
  && {
    background: linear-gradient(180deg, #0a1628 0%, #132240 100%);
    height: 100vh;
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    border-right: none;
    z-index: 10;
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .ant-menu {
    background: transparent !important;
    border-inline-end: none !important;
    color: rgba(255, 255, 255, 0.65);
  }

  .ant-menu-item {
    border-radius: 10px;
    margin-inline: 10px !important;
    width: calc(100% - 20px) !important;
    color: rgba(255, 255, 255, 0.85) !important;
    transition: all 0.2s;

    &:hover {
      background: rgba(44, 189, 255, 0.1) !important;
      color: #fff !important;
    }

    .anticon {
      color: ${PRIMARY};
      font-size: 16px;
      transition: color 0.2s;
    }

    &:hover .anticon {
      color: #5ad0ff;
    }
  }

  .ant-menu-item-selected {
    background: linear-gradient(135deg, ${PRIMARY} 0%, #1a9de0 100%) !important;
    color: #fff !important;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(44, 189, 255, 0.3);

    .anticon {
      color: #fff !important;
    }
  }

  .ant-menu-submenu-title {
    border-radius: 10px;
    margin-inline: 10px !important;
    width: calc(100% - 20px) !important;
    color: rgba(255, 255, 255, 0.85) !important;

    &:hover {
      background: rgba(44, 189, 255, 0.1) !important;
      color: #fff !important;
    }

    .anticon {
      color: ${PRIMARY};
    }
  }

  .ant-menu-submenu-open > .ant-menu-submenu-title {
    color: #fff !important;
    .anticon { color: #5ad0ff !important; }
  }

  .ant-menu-sub {
    background: transparent !important;
  }

  .ant-menu-inline-collapsed > .ant-menu-item,
  .ant-menu-inline-collapsed > .ant-menu-submenu > .ant-menu-submenu-title {
    margin-inline: 6px !important;
    width: calc(100% - 12px) !important;
    padding: 0 !important;
    padding-inline: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 40px !important;
    border-radius: 10px;
    overflow: hidden;

    .anticon {
      font-size: 18px !important;
      margin: 0 !important;
      line-height: 1 !important;
    }

    .ant-menu-item-icon + span,
    .ant-menu-title-content {
      display: none !important;
    }
  }
`;

const LogoArea = styled.div`
  text-align: center;
  padding: 20px 0 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const MenuScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const AccountSection = styled.div<{ $collapsed: boolean }>`
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: ${({ $collapsed }) => ($collapsed ? '8px 0' : '8px')};
`;

const AccountToggle = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ $collapsed }) => ($collapsed ? '10px 0' : '10px 12px')};
  justify-content: center;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const AvatarCircle = styled.div<{ $size?: number }>`
  width: ${({ $size }) => $size || 34}px;
  height: ${({ $size }) => $size || 34}px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${PRIMARY} 0%, #1a9de0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-weight: 700;
  font-size: ${({ $size }) => ($size ? Math.round($size * 0.38) : 13)}px;
`;

const AccountMenu = styled.div<{ $open: boolean }>`
  max-height: ${({ $open }) => ($open ? '200px' : '0')};
  overflow: hidden;
  transition: max-height 0.25s ease;
  ${({ $open }) => $open && 'margin-top: 4px;'}
  padding-left: 44px;
`;

const AccountLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${PRIMARY};
  }

  .anticon {
    color: ${PRIMARY};
    font-size: 14px;
  }
`;

const LogoutLink = styled(AccountLink)`
  color: ${DANGER.badge};
  .anticon { color: ${DANGER.badge}; }
  &:hover {
    color: ${DANGER.tag};
    background: color-mix(in srgb, ${DANGER.tag} 8%, transparent);
    .anticon { color: ${DANGER.tag}; }
  }
`;

const ChevronIcon = styled(UpOutlined)<{ $open: boolean }>`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(0)' : 'rotate(180deg)')};
  margin-left: auto;
`;

export const DesktopSiderMenu: React.FC<ISiderMenuProps> = ({ collapsed, onCollapse }) => {
  const { sessionStore } = useStore();
  const { currentLogin } = sessionStore;
  const [accountOpen, setAccountOpen] = useState(false);

  const initials = `${currentLogin.user?.name?.[0] || ''}${currentLogin.user?.surname?.[0] || ''}`.toUpperCase();

  return (
    <SiderWrapper
      theme="dark"
      collapsible
      trigger={null}
      width={256}
      collapsedWidth={68}
      collapsed={collapsed}
      onCollapse={onCollapse}
    >
      {/* The logo is the collapse control: it is the one thing always in the same place at the top
          of the sider, and it saves a hamburger in the header that does nothing else. */}
      <Tooltip placement="right" title={collapsed ? 'Expand menu' : 'Collapse menu'}>
        <LogoArea
          role="button"
          tabIndex={0}
          onClick={onCollapse}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onCollapse();
            }
          }}
        >
          {collapsed ? <SmallLogo /> : <BigLogo />}
        </LogoArea>
      </Tooltip>

      <MenuScroll>
        <SiderMenu />
      </MenuScroll>

      <AccountSection $collapsed={collapsed}>
        <AccountToggle
          $collapsed={collapsed}
          onClick={() => setAccountOpen((o) => !o)}
        >
          <AvatarCircle $size={collapsed ? 36 : 34}>{initials}</AvatarCircle>
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong ellipsis style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', display: 'block', lineHeight: '18px' }}>
                  {currentLogin.user?.name} {currentLogin.user?.surname}
                </Text>
                <Text ellipsis style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', display: 'block', lineHeight: '16px' }}>
                  {L('Manage account')}
                </Text>
              </div>
              <ChevronIcon $open={accountOpen} />
            </>
          )}
        </AccountToggle>

        <AccountMenu $open={accountOpen && !collapsed}>
          <AccountLink to="/my-account">
            <UserOutlined /> {!collapsed && L('My Account')}
          </AccountLink>
          <AccountLink to="/change-password">
            <EditOutlined /> {!collapsed && L('Change Password')}
          </AccountLink>
          <LogoutLink to="/logout">
            <LogoutOutlined /> {!collapsed && L('Logout')}
          </LogoutLink>
        </AccountMenu>
      </AccountSection>
    </SiderWrapper>
  );
};
